import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	EventEmitter,
	inject,
	input,
	NgZone,
	OnDestroy,
	Output,
	signal,
} from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { animationFrames, Subscription } from "rxjs";
import { distinctUntilChanged, map, takeWhile } from "rxjs/operators";

@Component({
	selector: "countdown",
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: "./countdown.component.scss",
	template: `
			@if (fullView()) {
				<div class="cd-container">
					<div class="d-flex d-lg-block">
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ days() }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.days"|translate}}</span></span>
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ pad(hours()) }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.hours"|translate}}</span></span>
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ pad(minutes()) }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.mins"|translate}}</span></span>
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ pad(seconds()) }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.secs"|translate}}</span></span>
				</div>
			</div>
			} @else {
					{{ pad(minutes()) }}:{{ pad(seconds()) }}
			}
	`, imports: [TranslatePipe]
})
export class CountdownComponent implements OnDestroy {
	private zone = inject(NgZone);
	private sub?: Subscription;
	private lastTimestamp = 0;

	fullView = input<boolean>(true);
	deadline = input.required<Date | string | number>();

	// Stato reattivo
	private remainingSeconds = signal(0);

	// Calcoli derivati
	days = computed(() => Math.floor(this.remainingSeconds() / 86400));
	hours = computed(() => Math.floor((this.remainingSeconds() % 86400) / 3600));

	minutes = computed(() =>
		this.fullView()
			? Math.floor((this.remainingSeconds() % 3600) / 60)
			: Math.floor(this.remainingSeconds() / 60)
	);

	seconds = computed(() => this.remainingSeconds() % 60);

	@Output() expired = new EventEmitter<void>();

	constructor() {
		// Reagisce al cambio della deadline in modo dichiarativo
		effect(() => {
			const newMs = (new Date(this.deadline())).getTime();

			// 1. Se la data è invalida o è la stessa di prima, ignora
			if (!(isNaN(newMs) || newMs === this.lastTimestamp)) {
				this.lastTimestamp = newMs;

				// 2. Facciamo ripartire il timer
				this.restart(newMs);
			}
		});
	}

	pad = (val: number): string => val.toString().padStart(2, "0");

	private restart(targetMs: number) {
		this.stop(); // Ferma quello precedente
		// 3. Esecuzione fuori dalla Zone per non appesantire il browser
		this.zone.runOutsideAngular(() => {
			this.sub = animationFrames().pipe(
				// Calcola la distanza nel tempo
				map(() => targetMs - Date.now()),
				// Converti in secondi (minimo 0)
				map(ms => Math.max(Math.floor(ms / 1000), 0)),
				// Notifica solo quando il secondo cambia effettivamente
				distinctUntilChanged(),
				// Continua finché > 0 (emette lo 0 grazie al true finale)
				takeWhile(sec => sec >= 0, true)
			).subscribe({
				next: sec => {
					// 4. Rientra in Angular solo per aggiornare lo schermo
					this.zone.run(() => {
						this.remainingSeconds.set(sec);
						if (sec === 0) {
							this.expired.emit();
							this.stop();
						}
					});
				},
				error: err => console.error("Countdown Error:", err)
			});
		});
	}

	private stop() {
		this.sub?.unsubscribe();
		this.sub = undefined;
	}

	ngOnDestroy() {
		this.stop();
	}
}